'use client'

/**
 * Hoja de papel de una herramienta, con sus acciones: guardar como imagen (Web
 * Share si el dispositivo lo permite, descarga si no) e imprimir.
 *
 * Compartido por todo lo que produce un artefacto en papel — la carta del ratón
 * y el certificado del diente —, que necesitan exactamente la misma mecánica:
 * canvas oculto, exportar a PNG, imprimir SOLO esta hoja y medir cómo salió.
 * Lo único propio de cada una es lo que se ve (`children`) y cómo se pinta en el
 * canvas (`draw`).
 *
 * La hoja visible es HTML, no el canvas: así se puede leer, seleccionar y
 * ampliar, y la impresión sale bien. El canvas está oculto y solo se usa para
 * generar el PNG en el momento de guardar.
 */

import { useRef, useState, type ReactNode } from 'react'
import { Download, Printer, Share2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { canvasToPngBlob } from '@/lib/guides/tools/letter-canvas'
import { printElement } from '@/lib/guides/tools/print-element'
import { shareOrDownload } from '@/lib/memory-video/share-or-download'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'

const COPY = {
  save: 'Guardar como imagen',
  saving: 'Preparando…',
  print: 'Imprimir',
  error: 'No hemos podido crear la imagen. Prueba a imprimirla.',
}

const ACTION_CLASSNAME =
  'rounded-full bg-white/15 px-6 py-5 text-base font-semibold text-white hover:bg-white/25'

export function PrintableSheet({
  children,
  className,
  draw,
  filename,
  savedEvent,
  analyticsProperties,
}: {
  /** Lo que se ve en el papel. */
  children: ReactNode
  /** Clases de la hoja (el papel en sí). */
  className: string
  /** Pinta el artefacto en el canvas oculto, justo antes de exportarlo. */
  draw: (canvas: HTMLCanvasElement) => void
  filename: string
  /** Evento con el que se mide la salida (`method`: web_share | download | print). */
  savedEvent: (typeof PRODUCT_EVENTS)[keyof typeof PRODUCT_EVENTS]
  /** Contexto del evento de guardado. Nunca datos del niño. */
  analyticsProperties: Record<string, unknown>
}) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(false)
  const sheetRef = useRef<HTMLElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  async function handleSave() {
    if (!canvasRef.current) return
    setSaving(true)
    setError(false)
    try {
      draw(canvasRef.current)
      const blob = await canvasToPngBlob(canvasRef.current)
      const outcome = await shareOrDownload({ blob, filename })
      // Un `cancelled` es el usuario cerrando la hoja de compartir: no se guardó
      // nada, así que contarlo inflaría la métrica.
      if (outcome !== 'cancelled') {
        captureProductEvent(savedEvent, {
          ...analyticsProperties,
          method: outcome === 'shared' ? 'web_share' : 'download',
        })
      }
    } catch {
      setError(true)
    } finally {
      setSaving(false)
    }
  }

  function handlePrint() {
    if (!sheetRef.current) return
    // Antes de imprimir, no después: `window.print()` bloquea hasta que se
    // cierra el diálogo. Ver la misma nota en halloween-booklet.
    captureProductEvent(savedEvent, { ...analyticsProperties, method: 'print' })
    printElement(sheetRef.current)
  }

  return (
    <div className="space-y-4">
      <figure ref={sheetRef} className={className}>
        {children}
      </figure>

      <canvas ref={canvasRef} className="hidden" aria-hidden />

      <div className="flex flex-wrap gap-3 print:hidden">
        <Button type="button" onClick={handleSave} disabled={saving} className={ACTION_CLASSNAME}>
          {saving ? <Download className="size-4" aria-hidden /> : <Share2 className="size-4" aria-hidden />}
          {saving ? COPY.saving : COPY.save}
        </Button>
        <Button type="button" onClick={handlePrint} className={ACTION_CLASSNAME}>
          <Printer className="size-4" aria-hidden />
          {COPY.print}
        </Button>
      </div>
      {error && <p className="text-sm text-red-300 print:hidden">{COPY.error}</p>}
    </div>
  )
}
